import { env } from './env';

// Filter values persisted in dashboards are numeric IDs against the blob
// dictionaries. They are stable across rebuilds because:
//   - regions: scripts/build_blob.py pre-fetches DISTINCT region_code ORDER BY
//     region_code before streaming, so indices are alphabetically sorted.
//   - occupations: same pre-fetched alphabetical pattern.
//   - age_bands / income_bands: filter values are the DB row id, not the array
//     position, and the header lists rows ORDER BY id.
//   - sex / economic_activity: indices come from hardcoded constants in
//     build_blob.py.
// If any of these change, dashboards persisted via web/src/state/dashboardStore
// will silently filter on bogus IDs.
const RECORD_SIZE = 35;
const SUPPORTED_SCHEMA_VERSION = '1.3';

type Pointer = {
  blob_filename: string;
  header_filename: string;
  uploaded_at: string;
};

export type AgeBandDef = {
  id: number;
  label: string;
  lower_age: number;
  upper_age: number | null;
};

export type IncomeBandDef = {
  id: number;
  label: string;
  lower_gbp: number | null;
  upper_gbp: number | null;
};

type Header = {
  schema_version: string;
  generated_at: string;
  person_count: number;
  record_size_bytes: number;
  record_format: string;
  endianness: string;
  regions: string[];
  first_names: string[];
  surnames: string[];
  occupations: string[];
  economic_activities: string[];
  age_bands: AgeBandDef[];
  income_bands: IncomeBandDef[];
  blob_filename: string;
  blob_size_bytes: number;
};

export type PopulationData = {
  length: number;
  positions: Float32Array;
  ageBands: Uint8Array;
  regionIndices: Uint16Array;
  firstNameIndices: Uint16Array;
  surnameIndices: Uint16Array;
  sex: Uint8Array;
  householdIncomeBand: Uint8Array;
  occupationIndices: Uint8Array;
  economicActivity: Uint8Array;
  ids: Uint8Array;
  getId: (i: number) => string;
  getName: (i: number) => string;
};

export type PopulationBlob = {
  data: PopulationData;
  regions: string[];
  firstNames: string[];
  surnames: string[];
  occupations: string[];
  economicActivities: string[];
  ageBands: AgeBandDef[];
  incomeBands: IncomeBandDef[];
  meta: {
    schemaVersion: string;
    generatedAt: string;
    personCount: number;
  };
};

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

function formatUuid(buf: Uint8Array, offset: number): string {
  return (
    HEX[buf[offset]] + HEX[buf[offset + 1]] + HEX[buf[offset + 2]] + HEX[buf[offset + 3]] + '-' +
    HEX[buf[offset + 4]] + HEX[buf[offset + 5]] + '-' +
    HEX[buf[offset + 6]] + HEX[buf[offset + 7]] + '-' +
    HEX[buf[offset + 8]] + HEX[buf[offset + 9]] + '-' +
    HEX[buf[offset + 10]] + HEX[buf[offset + 11]] + HEX[buf[offset + 12]] +
    HEX[buf[offset + 13]] + HEX[buf[offset + 14]] + HEX[buf[offset + 15]]
  );
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export async function loadPopulationBlob(): Promise<PopulationBlob> {
  const base = env.VITE_POPULATION_BLOB_URL.replace(/\/+$/, '');
  const t0 = performance.now();

  const pointer = await fetchJson<Pointer>(`${base}/current.json`);
  const header = await fetchJson<Header>(`${base}/${pointer.header_filename}`);

  if (header.schema_version !== SUPPORTED_SCHEMA_VERSION) {
    throw new Error(
      `Unsupported blob schema version ${header.schema_version}; expected ${SUPPORTED_SCHEMA_VERSION}.`
    );
  }
  if (header.record_size_bytes !== RECORD_SIZE) {
    throw new Error(
      `Unexpected record size ${header.record_size_bytes}; expected ${RECORD_SIZE}.`
    );
  }
  if (header.endianness !== 'little') {
    throw new Error(`Unsupported blob endianness: ${header.endianness}.`);
  }

  const binRes = await fetch(`${base}/${pointer.blob_filename}`);
  if (!binRes.ok) {
    throw new Error(
      `Failed to fetch blob ${pointer.blob_filename}: ${binRes.status} ${binRes.statusText}`
    );
  }
  const buffer = await binRes.arrayBuffer();

  const expectedBytes = header.person_count * RECORD_SIZE;
  if (buffer.byteLength !== expectedBytes) {
    throw new Error(
      `Blob size mismatch: got ${buffer.byteLength} bytes, expected ${expectedBytes} ` +
        `(${header.person_count} records x ${RECORD_SIZE}).`
    );
  }

  const view = new DataView(buffer);
  const src = new Uint8Array(buffer);
  const n = header.person_count;
  const positions = new Float32Array(n * 2);
  const ageBands = new Uint8Array(n);
  const regionIndices = new Uint16Array(n);
  const firstNameIndices = new Uint16Array(n);
  const surnameIndices = new Uint16Array(n);
  const sex = new Uint8Array(n);
  const householdIncomeBand = new Uint8Array(n);
  const occupationIndices = new Uint8Array(n);
  const economicActivity = new Uint8Array(n);
  const ids = new Uint8Array(n * 16);

  for (let i = 0; i < n; i++) {
    const o = i * RECORD_SIZE;
    ids.set(src.subarray(o, o + 16), i * 16);
    positions[i * 2] = view.getFloat32(o + 16, true);
    positions[i * 2 + 1] = view.getFloat32(o + 20, true);
    ageBands[i] = src[o + 24];
    regionIndices[i] = view.getUint16(o + 25, true);
    firstNameIndices[i] = view.getUint16(o + 27, true);
    surnameIndices[i] = view.getUint16(o + 29, true);
    sex[i] = src[o + 31];
    householdIncomeBand[i] = src[o + 32];
    occupationIndices[i] = src[o + 33];
    economicActivity[i] = src[o + 34];
  }

  const firstNames = header.first_names;
  const surnames = header.surnames;

  const getName = (i: number): string => {
    const first = firstNames[firstNameIndices[i]] ?? '';
    const last = surnames[surnameIndices[i]] ?? '';
    if (!first && !last) return '';
    if (!first) return last;
    if (!last) return first;
    return `${first} ${last}`;
  };

  const elapsed = Math.round(performance.now() - t0);
  console.info(
    `[blobLoader] parsed ${n.toLocaleString()} records, ` +
      `${header.regions.length.toLocaleString()} regions, ` +
      `${firstNames.length.toLocaleString()} first names, ` +
      `${surnames.length.toLocaleString()} surnames, ` +
      `${header.occupations.length.toLocaleString()} occupations, ` +
      `${header.economic_activities.length.toLocaleString()} economic activities, ` +
      `${elapsed} ms`
  );

  return {
    data: {
      length: n,
      positions,
      ageBands,
      regionIndices,
      firstNameIndices,
      surnameIndices,
      sex,
      householdIncomeBand,
      occupationIndices,
      economicActivity,
      ids,
      getId: (i: number) => formatUuid(ids, i * 16),
      getName,
    },
    regions: header.regions,
    firstNames,
    surnames,
    occupations: header.occupations,
    economicActivities: header.economic_activities,
    ageBands: header.age_bands,
    incomeBands: header.income_bands,
    meta: {
      schemaVersion: header.schema_version,
      generatedAt: header.generated_at,
      personCount: header.person_count,
    },
  };
}
